import { useContext, useEffect, useState } from "react";
import UserContext from "../../context/UserContext";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import TopNav from "../common/TopNav";
import { deleteAccount, getPlanInfo, getUserConfig, putUserConfig } from "../../services/client";
import { timezones } from "../../helpers/TimezonesData";
import Container from "../Elements/Container";
import AlertComponent from "../Elements/AlertComponent";
import { saveUserInfoInLocalStorage } from "../../services/auth";
import PasswordChange from "./PasswordChange";
import PlacesAutocomplete from "../OrganizationSettings/PlacesAutocomplete";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

const useStyles = makeStyles((theme) => {
  const radius = theme.borderRadiusBoxes?.borderRadius || 4;
  const fieldRadius = radius;
  const buttonRadius = radius;
  const dialogRadius = radius;

  return {
    page: {
      width: "100%",
      boxSizing: "border-box",
      overflowX: "hidden",
      height: "calc(100vh - 116px)",
      minHeight: "calc(100vh - 116px)",
      padding: theme.spacing(2.5, 1.5, 3),
      [theme.breakpoints.down("sm")]: {
        height: "auto",
        minHeight: "auto",
        padding: theme.spacing(2, 1.5, 2.5),
      },
    },
    layout: {
      width: "100%",
      maxWidth: "min(1840px, calc(100vw - 28px))",
      height: "100%",
      minHeight: 0,
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "minmax(360px, 430px) minmax(0, 1fr)",
      gap: theme.spacing(3.5),
      alignItems: "stretch",
      [theme.breakpoints.down("md")]: {
        maxWidth: "100%",
        height: "auto",
        gridTemplateColumns: "1fr",
      },
    },
    heroCard: {
      padding: theme.spacing(3, 3),
      borderRadius: radius,
      overflow: "hidden",
      border: `1px solid ${theme.palette.border.main}`,
      background: `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${theme.palette.surface.panel} 100%)`,
      boxShadow: "0 18px 44px rgba(15, 23, 42, 0.08)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      height: "100%",
      minHeight: 0,
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2.5, 2),
        height: "auto",
      },
    },
    heroHeader: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
    },
    heroIcon: {
      width: 46,
      height: 46,
      minWidth: 46,
      borderRadius: radius,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.surface.accentSoft,
      border: `1px solid ${theme.palette.surface.accentBorder}`,
      boxShadow: theme.palette.shadow.accent,
    },
    heroRow: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    heroCopy: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.75),
    },
    eyebrow: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: theme.palette.text.section,
    },
    heroTitle: {
      color: theme.palette.text.primary,
      fontWeight: 700,
      lineHeight: 1.2,
    },
    heroDescription: {
      color: theme.palette.text.secondary,
      lineHeight: 1.55,
      fontSize: 14,
    },
    demoBanner: {
      padding: theme.spacing(1.25, 1.5),
      borderRadius: fieldRadius,
      border: `1px solid ${theme.palette.border.soft}`,
      backgroundColor: theme.palette.surface.alt,
      color: theme.palette.text.secondary,
      fontSize: 13,
      lineHeight: 1.5,
    },
    heroMetrics: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
    },
    metricCard: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.25),
      padding: theme.spacing(1.15, 1.25),
      borderRadius: radius,
      border: `1px solid ${theme.palette.border.soft}`,
      backgroundColor: theme.palette.common.white,
      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
    },
    metricIcon: {
      width: 34,
      height: 34,
      minWidth: 34,
      borderRadius: radius,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.surface.accentSoft,
    },
    metricMeta: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    metricLabel: {
      color: theme.palette.text.secondary,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      fontWeight: 700,
    },
    metricValue: {
      color: theme.palette.text.primary,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.35,
      wordBreak: "break-word",
    },
    heroActions: {
      marginTop: "auto",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    formCard: {
      padding: theme.spacing(2.75, 3.25),
      borderRadius: radius,
      overflow: "hidden",
      border: `1px solid ${theme.palette.border.main}`,
      backgroundColor: theme.palette.common.white,
      boxShadow: "0 18px 44px rgba(15, 23, 42, 0.08)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100%",
      minHeight: 0,
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2.25, 1.75),
        height: "auto",
      },
    },
    sectionRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      flexWrap: "wrap",
      marginBottom: theme.spacing(2.25),
    },
    sectionCopy: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.25),
    },
    sectionIcon: {
      width: 40,
      height: 40,
      minWidth: 40,
      borderRadius: radius,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.surface.accentSoft,
      border: `1px solid ${theme.palette.surface.accentBorder}`,
    },
    sectionText: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.35),
    },
    sectionTitle: {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
    sectionDescription: {
      color: theme.palette.text.secondary,
      fontSize: 13,
      lineHeight: 1.5,
    },
    sectionAside: {
      maxWidth: 320,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-end",
      paddingTop: theme.spacing(0.5),
      [theme.breakpoints.down("sm")]: {
        maxWidth: "100%",
        width: "100%",
        justifyContent: "flex-start",
        paddingTop: 0,
      },
    },
    sectionHint: {
      color: theme.palette.text.secondary,
      fontSize: 13,
      lineHeight: 1.5,
      textAlign: "right",
      [theme.breakpoints.down("sm")]: {
        textAlign: "left",
      },
    },
    circular: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(5, 0),
      flex: 1,
    },
    loadingState: {
      gridColumn: "1 / -1",
      height: "100%",
      minHeight: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: radius,
      border: `1px solid ${theme.palette.border.main}`,
      background: `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${theme.palette.surface.soft} 100%)`,
      boxShadow: "0 18px 44px rgba(15, 23, 42, 0.08)",
      [theme.breakpoints.down("md")]: {
        minHeight: 320,
      },
    },
    formGrid: {
      width: "100%",
      margin: 0,
      alignContent: "flex-start",
    },
    formBody: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      justifyContent: "space-between",
    },
    fieldItem: {
      display: "flex",
      alignItems: "stretch",
      "& > *": {
        width: "100%",
      },
    },
    field: {
      width: "100%",
      borderRadius: fieldRadius,
      "& .MuiInputBase-root": {
        backgroundColor: theme.palette.common.white,
        borderRadius: fieldRadius,
        overflow: "hidden",
        transition: "box-shadow 160ms ease, border-color 160ms ease",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: fieldRadius,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.border.input,
        borderRadius: fieldRadius,
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.light,
      },
      "& .MuiOutlinedInput-root.Mui-focused": {
        boxShadow: `0 0 0 3px ${theme.palette.surface.accentSoft}`,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "& .MuiInputLabel-root": {
        color: theme.palette.text.secondary,
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 0,
      },
    },
    fieldControl: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "stretch",
    },
    secondaryButton: {
      borderRadius: buttonRadius,
      overflow: "hidden",
      textTransform: "none",
      fontWeight: 600,
      boxShadow: "none",
      padding: theme.spacing(1, 2),
      borderColor: theme.palette.border.button,
      color: theme.palette.text.secondary,
    },
    primaryButton: {
      minWidth: 150,
      borderRadius: buttonRadius,
      overflow: "hidden",
      textTransform: "none",
      fontWeight: 600,
      boxShadow: "none",
      padding: theme.spacing(1, 2.5),
    },
    dangerButton: {
      borderRadius: buttonRadius,
      overflow: "hidden",
      textTransform: "none",
      fontWeight: 600,
      boxShadow: "none",
      padding: theme.spacing(1, 2),
    },
    footerActions: {
      marginTop: theme.spacing(2.5),
      display: "flex",
      justifyContent: "flex-end",
    },
    dialogPaper: {
      borderRadius: dialogRadius,
      overflow: "hidden",
      border: `1px solid ${theme.palette.border.dialog}`,
      boxShadow: theme.palette.shadow.dialog,
    },
    dialogTitle: {
      paddingBottom: theme.spacing(1),
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
    dialogContent: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    dialogBody: {
      color: theme.palette.text.secondary,
      lineHeight: 1.7,
    },
    warningCopy: {
      color: theme.palette.text.secondary,
      lineHeight: 1.7,
    },
    dialogActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
      flexWrap: "wrap",
      marginTop: theme.spacing(3),
    },
  };
});

function UserConfig({ handleLogout }) {
  const classes = useStyles();
  const { plan } = useContext(UserContext);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [deletePanel, setDeletePanel] = useState(false);
  const [deleteVerificationValue, setDeleteVerificationValue] = useState("");
  const [stringVerification, setStingVerification] = useState(true);
  const [close, setClose] = useState(false);
  const [msg, setMsg] = useState({
    msg: "",
    type: "",
  });
  const [openPasswordChange, setOpenPasswordChange] = useState(false);
  const [warningModal, setWarningModal] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const NO_ESPECIFICADO = "No Especificado";
  const isDemoPlan = plan?.info?.plan === "Demo";

  useEffect(() => {
    const fetchUserConfig = async () => {
      try {
        setLoading(true);
        const res = await getUserConfig();
        const data = {
          ...res.data,
          address: res.data.direccion,
        };
        setConfig(data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };

    fetchUserConfig();
  }, []);

  const handleChange = (e) => {
    setConfig({ ...config, [e.target.name]: e.target.value });
  };

  const doSave = async () => {
    let successAlertTimeout;
    try {
      setLoading(true);
      successAlertTimeout = setTimeout(() => {
        setMsg({
          msg: "Se han guardado sus cambios con exito.",
          type: "success",
        });
        setClose(true);
        setLoading(false);
      }, 700);

      const configToSend = { ...config };

      if (configToSend.address) {
        configToSend.direccion = configToSend.address;
      }

      delete configToSend.address;
      delete configToSend.coordinates;

      await putUserConfig(configToSend);

      const res2 = await getPlanInfo();
      saveUserInfoInLocalStorage(res2.data);
    }
    catch (error) {
      console.error(error);
      clearTimeout(successAlertTimeout);
      setLoading(false);

      setClose(true);
      setMsg({
        msg: "Ocurrio un error inesperado.",
        type: "error",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !config.razon_social ||
      !config.codigo_postal ||
      (!config.direccion && !config.address) ||
      config.coordinates === "0,0" ||
      !config.cuit ||
      !config.condicion_fiscal ||
      config.condicion_fiscal === NO_ESPECIFICADO
    ) {
      setWarningModal(true);
      setPendingSave(true);
      return;
    }
    await doSave();
  };

  const handleDeleteChange = (e) => {
    const value = e.target.value;
    setDeleteVerificationValue(value);
    setStingVerification(value !== "Eliminar cuenta");
  };

  const handleCloseDeletePanel = () => {
    setDeletePanel(false);
    setDeleteVerificationValue("");
    setStingVerification(true);
  };

  const handleDeleteSubmit = async () => {
    try {
      await deleteAccount();
      handleLogout();
    } catch (error) {
      setClose(true);
      setMsg({
        msg: error?.response?.data?.msg,
        type: "error",
      });
    }
  };

  const handleCloseWarning = () => {
    setWarningModal(false);
    setPendingSave(false);
  };

  const getErrors = () => {
    let errors = false;
    if (config.email === "") {
      errors = true;
    }
    return errors;
  };

  const capitalizeString = (str) => {
    return str
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getLabel = (key) => {
    const labels = {
      razon_social: "Razon social",
      codigo_postal: "Codigo postal",
      condicion_fiscal: "Condicion fiscal",
      cuit: "CUIT",
    };

    return labels[key] || capitalizeString(key);
  };

  const handleOpenPasswordChange = () => {
    setOpenPasswordChange(!openPasswordChange);
  };

  const visibleConfigKeys = Object.keys(config).filter(
    (key) => key !== "timezone" && key !== "coordinates" && key !== "address"
  );

  const metricItems = [
    {
      icon: <AlternateEmailOutlinedIcon fontSize="small" />,
      label: "Email",
      value: config.email || "Sin definir",
    },
    {
      icon: <LanguageOutlinedIcon fontSize="small" />,
      label: "Timezone",
      value: config.timezone || "Sin definir",
    },
    {
      icon: <BusinessOutlinedIcon fontSize="small" />,
      label: "Razon social",
      value: config.razon_social || "Completa tus datos fiscales",
    },
  ];

  const getFieldColumns = (key) => {
    if (key === "direccion") {
      return 12;
    }

    if (key === "email" || key === "razon_social") {
      return 6;
    }

    return 6;
  };

  return (
    <>
      <TopNav titulo="Preferencias" subtitulo=" " />

      <AlertComponent severity={msg.type} msg={msg.msg} setClose={setClose} close={close} />

      <Dialog
        open={openPasswordChange}
        onClose={handleOpenPasswordChange}
        aria-labelledby="password-change-title"
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle id="password-change-title" className={classes.dialogTitle}>
          Cambiar contraseña
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <PasswordChange
            handleOpenPasswordChange={handleOpenPasswordChange}
            setMsg={setMsg}
            setClose={setClose}
          />
        </DialogContent>
      </Dialog>

      <Container>
        <Box className={classes.page}>
          <Box className={classes.layout}>
            {loading ? (
              <Paper elevation={0} className={classes.loadingState}>
                <div className={classes.circular}>
                  <CircularProgress />
                </div>
              </Paper>
            ) : (
              <>
                <Paper elevation={0} className={classes.heroCard}>
                  <Box className={classes.heroRow}>
                    <Box className={classes.heroHeader}>
                      <Box className={classes.heroIcon}>
                        <ManageAccountsOutlinedIcon />
                      </Box>
                      <Box className={classes.heroCopy}>
                        <Typography className={classes.eyebrow}>Cuenta</Typography>
                        <Typography variant="h6" className={classes.heroTitle}>
                          Preferencias de la cuenta
                        </Typography>
                        <Typography className={classes.heroDescription}>
                          Gestiona la informacion principal de tu cuenta de forma simple, clara y
                          ordenada.
                        </Typography>
                      </Box>
                    </Box>

                    <Box className={classes.heroMetrics}>
                      {metricItems.map((item) => (
                        <Box key={item.label} className={classes.metricCard}>
                          <Box className={classes.metricIcon}>{item.icon}</Box>
                          <Box className={classes.metricMeta}>
                            <Typography className={classes.metricLabel}>{item.label}</Typography>
                            <Typography className={classes.metricValue}>{item.value}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {isDemoPlan && (
                    <Box className={classes.demoBanner}>
                      Esta cuenta esta en modo demo. Puedes revisar la informacion, pero no se aplicaran
                      cambios permanentes.
                    </Box>
                  )}

                  <Box className={classes.heroActions}>
                    <Button
                      variant="outlined"
                      onClick={handleOpenPasswordChange}
                      className={classes.secondaryButton}
                      startIcon={<LockOutlinedIcon fontSize="small" />}
                    >
                      Cambiar contraseña
                    </Button>

                    {!isDemoPlan && (
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => setDeletePanel(true)}
                        className={classes.dangerButton}
                        startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                      >
                        Eliminar cuenta
                      </Button>
                    )}
                  </Box>
                </Paper>

                <Dialog
                  open={deletePanel}
                  onClose={handleCloseDeletePanel}
                  aria-labelledby="delete-account-title"
                  fullWidth
                  maxWidth="sm"
                  PaperProps={{ className: classes.dialogPaper }}
                >
                  <DialogTitle id="delete-account-title" className={classes.dialogTitle}>
                    Eliminar cuenta
                  </DialogTitle>
                  <DialogContent className={classes.dialogContent}>
                    <Typography className={classes.dialogBody}>
                      El proceso de eliminacion es irreversible y representara la perdida total de la
                      informacion asociada a la cuenta, incluyendo las mediciones de tus medidores.
                    </Typography>
                    <Box mt={2}>
                      <Typography className={classes.dialogBody}>
                        Si eliminas la cuenta, solo podras crear otra con el mismo correo despues de 15
                        dias.
                      </Typography>
                    </Box>
                    <Box mt={3}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label='Escribe "Eliminar cuenta"'
                        value={deleteVerificationValue}
                        onChange={handleDeleteChange}
                        className={classes.field}
                      />
                    </Box>
                    <Box className={classes.dialogActions}>
                      <Button
                        onClick={handleCloseDeletePanel}
                        variant="outlined"
                        className={classes.secondaryButton}
                      >
                        Cancelar
                      </Button>
                      <Button
                        disabled={stringVerification}
                        onClick={handleDeleteSubmit}
                        variant="contained"
                        color="secondary"
                        className={classes.dangerButton}
                      >
                        Eliminar cuenta
                      </Button>
                    </Box>
                  </DialogContent>
                </Dialog>

                <Paper elevation={0} className={classes.formCard}>
                  <Box className={classes.sectionRow}>
                    <Box className={classes.sectionCopy}>
                      <Box className={classes.sectionIcon}>
                        <ManageAccountsOutlinedIcon fontSize="small" />
                      </Box>
                      <Box className={classes.sectionText}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                          Datos del perfil
                        </Typography>
                        <Typography className={classes.sectionDescription}>
                          Actualiza tus datos personales, fiscales y de ubicacion desde un solo lugar.
                        </Typography>
                      </Box>
                    </Box>

                    <Box className={classes.sectionAside}>
                      <Typography className={classes.sectionHint}>
                        Revisa la informacion y guarda los cambios cuando este todo listo.
                      </Typography>
                    </Box>
                  </Box>

                  <form onSubmit={handleSubmit} className={classes.formBody}>
                    <Grid container spacing={2} className={classes.formGrid}>
                      {visibleConfigKeys.map((key) => {
                        if (key === "direccion") {
                          return (
                            <Grid item xs={12} md={12} key={key} className={classes.fieldItem}>
                              <Box className={classes.fieldControl}>
                                <PlacesAutocomplete
                                  extraField={config}
                                  setExtraField={setConfig}
                                  label="Direccion"
                                  textFieldProps={{
                                    className: classes.field,
                                    size: "small",
                                  }}
                                />
                              </Box>
                            </Grid>
                          );
                        }

                        if (key === "condicion_fiscal") {
                          return (
                            <Grid
                              item
                              xs={12}
                              md={getFieldColumns(key)}
                              key={key}
                              className={classes.fieldItem}
                            >
                              <Box className={classes.fieldControl}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label={getLabel(key)}
                                  name="condicion_fiscal"
                                  value={config.condicion_fiscal || NO_ESPECIFICADO}
                                  onChange={handleChange}
                                  disabled={isDemoPlan}
                                  error={config.condicion_fiscal === NO_ESPECIFICADO}
                                  className={classes.field}
                                >
                                  <MenuItem value="Responsable Inscripto">Responsable Inscripto</MenuItem>
                                  <MenuItem value="Monotributista">Monotributista</MenuItem>
                                  <MenuItem value="Exento">Exento</MenuItem>
                                  <MenuItem value="Consumidor Final">Consumidor Final</MenuItem>
                                  <MenuItem value="Responsable No Inscripto">
                                    Responsable no Inscripto
                                  </MenuItem>
                                  <MenuItem value="Monotributista Social">
                                    Monotributista Social
                                  </MenuItem>
                                  <MenuItem value="Sujeto No Categorizado">
                                    Sujeto no Categorizado
                                  </MenuItem>
                                  <MenuItem value="Exportador">Exportador</MenuItem>
                                  <MenuItem value="No Especificado">No especificado</MenuItem>
                                </TextField>
                              </Box>
                            </Grid>
                          );
                        }

                        return (
                          <Grid
                            item
                            xs={12}
                            md={getFieldColumns(key)}
                            key={key}
                            className={classes.fieldItem}
                          >
                            <Box className={classes.fieldControl}>
                              <TextField
                                disabled={key === "email" ? true : isDemoPlan}
                                className={classes.field}
                                variant="outlined"
                                size="small"
                                placeholder={getLabel(key)}
                                label={getLabel(key)}
                                name={key}
                                value={config[key] || ""}
                                onChange={handleChange}
                                fullWidth
                                color="primary"
                              />
                            </Box>
                          </Grid>
                        );
                      })}

                      <Grid item xs={12} md={6} className={classes.fieldItem}>
                        <Box className={classes.fieldControl}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Timezone"
                            name="timezone"
                            value={config.timezone || ""}
                            onChange={handleChange}
                            disabled={isDemoPlan}
                            className={classes.field}
                          >
                            <MenuItem value="" />
                            {Object.keys(timezones).map((key) => (
                              <MenuItem key={key} value={timezones[key]}>
                                {key}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box className={classes.footerActions}>
                      {!isDemoPlan && (
                        <Button
                          type="submit"
                          color="primary"
                          variant="contained"
                          disabled={getErrors()}
                          className={classes.primaryButton}
                        >
                          Guardar cambios
                        </Button>
                      )}
                    </Box>
                  </form>
                </Paper>
              </>
            )}
          </Box>
        </Box>
      </Container>

      <Dialog
        open={warningModal}
        onClose={handleCloseWarning}
        aria-labelledby="warning-dialog-title"
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle id="warning-dialog-title" className={classes.dialogTitle}>
          Advertencia
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography className={classes.warningCopy}>
            Si no completas los campos <b>Razon Social</b>, <b>Codigo Postal</b>, <b>Direccion</b>,
            <b> CUIT</b> y <b> Condicion Fiscal</b> no podras realizar pagos.
          </Typography>
          <Box className={classes.dialogActions}>
            <Button onClick={handleCloseWarning} variant="outlined" className={classes.secondaryButton}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                setWarningModal(false);
                if (pendingSave) {
                  setPendingSave(false);
                  await doSave();
                }
              }}
              color="primary"
              variant="contained"
              className={classes.primaryButton}
            >
              Aceptar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UserConfig;
